import { defineStore } from "pinia";
import { ProfileAdministrationAPI } from "../api";
import { DirectLink, Project, ShortLinkRequest } from "../types/PersologOnlineTypes.ts";
import { getProductQuantity } from "../utils/ProjectUtils.ts";

type DirectLinkDetailsState = {
    directLinkCreationVisible: boolean;
    directLinkEditingVisible: boolean;
    createDirectLinkWarningDialogVisible: boolean;
    deleteDirectLinkWarningDialogVisible: boolean;
    availableQuantity: number;
    directLinkAmount: number;
};

export const useProjectDetailsDirectLinkStore = defineStore("projectDetailsDirectLink", {
    state: (): DirectLinkDetailsState => ({
        directLinkCreationVisible: false,
        directLinkEditingVisible: false,
        createDirectLinkWarningDialogVisible: false,
        deleteDirectLinkWarningDialogVisible: false,
        availableQuantity: 0,
        directLinkAmount: 1,
    }),
    actions: {
        async openCreateDialog(isAllowed: boolean, trainerId: string, defaultProduct: string | undefined) {
            await this.refreshAvailableQuantity(trainerId, defaultProduct);
            this.createDirectLinkWarningDialogVisible = !isAllowed;
            this.directLinkCreationVisible = isAllowed;
        },
        async openEditDialog(trainerId: string, defaultProduct: string | undefined) {
            await this.refreshAvailableQuantity(trainerId, defaultProduct);
            this.directLinkEditingVisible = true;
        },
        closeCreateDialog() {
            this.directLinkCreationVisible = false;
        },
        closeEditDialog() {
            this.directLinkEditingVisible = false;
        },
        closeCreateWarning() {
            this.createDirectLinkWarningDialogVisible = false;
        },
        openDeleteWarning() {
            this.deleteDirectLinkWarningDialogVisible = true;
        },
        closeDeleteWarning() {
            this.deleteDirectLinkWarningDialogVisible = false;
        },
        /**
         * The project overview hands surface-backed items around whose core-only
         * fields hold placeholder defaults until the detail fetch resolves — an
         * empty participantRetentionPeriod fails the backend's Period parsing on
         * the direct-link endpoints. Re-fetch the full project in that case so
         * the payload always carries the real core data.
         */
        async withCoreData(project: Project, trainerId: string): Promise<Project> {
            if (project.participantRetentionPeriod || project.id === undefined) {
                return project;
            }
            return ProfileAdministrationAPI.getProject(trainerId, project.id);
        },
        async refreshAvailableQuantity(trainerId: string, defaultProduct: string | undefined) {
            if (defaultProduct) {
                this.availableQuantity = await getProductQuantity(trainerId, defaultProduct);
                return;
            }

            this.availableQuantity = 0;
        },
        async createDirectLink(project: Project, amount: number, trainerId: string, expiresAt: string | null = null) {
            if (project.id === undefined) {
                throw new Error("Project ID is required to create a direct link.");
            }

            this.directLinkAmount = amount;
            const newLink: DirectLink = {
                project: await this.withCoreData(project, trainerId),
                quantity: this.directLinkAmount,
                expiresAt,
            };
            await ProfileAdministrationAPI.createDirectLinkForExistingProject(newLink);
            this.closeCreateDialog();
            const updatedProject = await ProfileAdministrationAPI.getProject(trainerId, project.id);
            await this.refreshAvailableQuantity(trainerId, project.defaultProduct);
            return updatedProject;
        },
        async editDirectLink(
            project: Project,
            directLink: DirectLink,
            amount: number,
            trainerId: string,
            expiresAt: string | null = null,
        ) {
            if (project.id === undefined) {
                throw new Error("Project ID is required to edit a direct link.");
            }

            const newLink: DirectLink = {
                project: await this.withCoreData(project, trainerId),
                quantity: amount,
                hash: directLink.hash,
                id: directLink.id,
                expiresAt,
            };

            await ProfileAdministrationAPI.updateDirectLink(newLink);
            this.closeEditDialog();
            const updatedProject = await ProfileAdministrationAPI.getProject(trainerId, project.id);
            await this.refreshAvailableQuantity(trainerId, project.defaultProduct);
            return updatedProject;
        },
        async deleteDirectLink(project: Project, trainerId: string) {
            if (project.id === undefined) {
                throw new Error("Project ID is required to delete a direct link.");
            }

            await ProfileAdministrationAPI.deleteDirectLink(project.id);
            this.closeDeleteWarning();
            const updatedProject = await ProfileAdministrationAPI.getProject(trainerId, project.id);
            await this.refreshAvailableQuantity(trainerId, project.defaultProduct);
            return updatedProject;
        },
        async createShortLink(project: Project, trainerId: string, request: ShortLinkRequest) {
            if (project.id === undefined) {
                throw new Error("Project ID is required to create a short link.");
            }

            await ProfileAdministrationAPI.createDirectLinkShortLink(project.id, request);
            // Reload the project so its refreshed surface (with the new short link) drives directLinkMap.
            return ProfileAdministrationAPI.getProject(trainerId, project.id);
        },
        async deleteShortLink(project: Project, trainerId: string) {
            if (project.id === undefined) {
                throw new Error("Project ID is required to delete a short link.");
            }

            await ProfileAdministrationAPI.deleteDirectLinkShortLink(project.id);
            return ProfileAdministrationAPI.getProject(trainerId, project.id);
        },
    },
});
